import {
  IonButton,
  IonChip,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonLabel,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  RefresherEventDetail,
  useIonAlert,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import React, { useCallback, useContext, useState } from "react";
import PageHeader from "../components/PageHeader";
import {
  pencilOutline,
  pencilSharp,
  refreshOutline,
  refreshSharp,
  trashOutline,
  trashSharp,
} from "ionicons/icons";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import {
  ADDING_TIME_ENTRY,
  COMPLETING_INSPECTION,
  COMPLETING_WO,
  CREATE_WO,
  ISSUE_MATERIAL,
  OperationDetails,
  RETURN_MATERIAL,
  StatusType,
  TimeEntry as TimeEntryProps,
  useWoOfflineUpdatesStore,
} from "../store/wo-offline-updates";
import useSyncOfflineUpdates from "../hooks/offline/useSyncOfflineUpdates";
import { useShallow } from "zustand/react/shallow";
import { useHistory } from "react-router";
import TimeEntry from "./TimeEntry";

const getStatusColor = (status: StatusType): string => {
  switch (status) {
    case "pending":
      return "medium";
    case "uploading":
      return "warning";
    case "success":
      return "success";
    case "failed":
      return "danger";
    default:
      return "medium";
  }
};

const OfflineUploadData: React.FC = () => {
  const [updateTimeEntry, setUpdateTimeEntry] = useState<
    TimeEntryProps | undefined
  >(undefined);
  const { translations } = useContext(TranslationsContext);
  const { syncOfflineUpdates, isSyncing } = useSyncOfflineUpdates();
  const history = useHistory();
  const data = useWoOfflineUpdatesStore(
    useShallow((state) => state.operationDetails)
  );
  const deleteById = useWoOfflineUpdatesStore((state) => state.deleteById);
  const deleteAllSuccessFullOps = useWoOfflineUpdatesStore(
    (state) => state.deleteAllSuccessFullOps
  );
  const makeOperationCompletedById = useWoOfflineUpdatesStore(
    (state) => state.makeOperationCompletedById
  );
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [presentAlert] = useIonAlert();
  const [, setInRefresh] = useState(false);
  const [, forceRefresh] = useState(new Date());

  useIonViewWillLeave(() => {
    // Cleanup here instead of useEffect cleanup
    deleteAllSuccessFullOps();
  });

  useIonViewWillEnter(() => {
    //reset the cache
    forceRefresh(new Date());
  });

  const handleUpdateData = useCallback(
    (ops: OperationDetails) => {
      const { id, operation, followUpToWoNumber } = ops;
      if (operation === CREATE_WO) {
        const wo = useWoOfflineUpdatesStore.getState().getWoToCreateById(id);
        const equipId = wo?.requestObject.Equipment;
        if (followUpToWoNumber)
          history.push(
            `/quick-work-order/true/${equipId}/${followUpToWoNumber}/update/${id}`
          );
        else history.push(`/quick-work-order/true/${equipId}/update/${id}`);
      } else if (operation === ADDING_TIME_ENTRY) {
        setUpdateTimeEntry(
          useWoOfflineUpdatesStore.getState().getTimeEntryById(id)
        );
      } else if (operation === COMPLETING_WO) {
        const wo = useWoOfflineUpdatesStore.getState().getWoToCompleteById(id);
        if (wo) history.push(`/workorder/${wo.woNumber}/completiondata/${id}`);
      } else if (operation === ISSUE_MATERIAL) {
        const issue = useWoOfflineUpdatesStore.getState().getIssueById(id);
        if (issue)
          history.push(`/workorder/${issue.woNumber}/issuematerial/${id}`);
      } else if (operation === RETURN_MATERIAL) {
        const returnData = useWoOfflineUpdatesStore
          .getState()
          .getReturnById(id);
        if (returnData)
          history.push(
            `/workorder/${returnData.woNumber}/returnmaterial/${id}`
          );
      } else if (operation === COMPLETING_INSPECTION) {
        const inspection = useWoOfflineUpdatesStore
          .getState()
          .getInspectionById(id);
        if (inspection)
          history.push(
            `/workorder/offline/${inspection.woNumber}/inspection/${inspection.inspectionId}/${id}`
          );
      }
    },
    [history]
  );

  const handleDelete = useCallback(
    (id: string) => {
      presentAlert({
        message:
          translatedMessages["MobOfflineDataDeletionMsg"]?.MessageText ||
          "Are you sure you want to delete this?",
        buttons: [
          {
            text: translations["lbl_btn_cancel"] || "Cancel",
            role: "cancel",
          },
          {
            text: translations["lbl_btn_delete"] || "Delete",
            role: "destructive",
            handler: () => {
              deleteById(id);
            },
          },
        ],
      });
    },
    [deleteById, presentAlert, translatedMessages, translations]
  );

  const handleRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    setInRefresh(true);
    event.detail.complete();
  };

  //need this logging for ios issue debugging
  console.log("Offline Operations", data);

  return (
    <>
      <IonPage>
        <PageHeader
          title={
            translations["lbl_mob_offline_upload_data"] || "Offline Upload Data"
          }
        >
          <IonButton
            onClick={syncOfflineUpdates}
            slot="end"
            fill="clear"
            size="small"
            disabled={isSyncing}
          >
            <IonIcon
              ios={refreshSharp}
              md={refreshOutline}
              slot="icon-only"
            ></IonIcon>
          </IonButton>
        </PageHeader>

        <IonContent fullscreen>
          <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
            <IonRefresherContent></IonRefresherContent>
          </IonRefresher>
          <IonGrid>
            <IonRow class="ion-padding-vertical">
              <IonCol>
                <strong>WO Number</strong>
              </IonCol>
              <IonCol>
                <strong>Operation Name</strong>
              </IonCol>
              <IonCol>
                <strong>Error</strong>
              </IonCol>
              <IonCol>
                <strong>Status</strong>
              </IonCol>
              <IonCol>
                <strong>Actions</strong>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="12">
                <div
                  style={{
                    height: "1px",
                    backgroundColor: "var(--ion-color-light)",
                  }}
                />
              </IonCol>
            </IonRow>

            <div
              style={{
                padding: 8,
              }}
              hidden={data.length > 0}
            >
              <IonLabel color={"warning"}>
                {translatedMessages["NoResultsFound"]?.MessageText ||
                  "No results found."}
              </IonLabel>
            </div>

            {data.map((item) => (
              <React.Fragment key={item.id}>
                <IonRow>
                  <IonCol style={{ display: "flex", alignItems: "center" }}>
                    <IonLabel>{item.woNumber ?? ""}</IonLabel>
                  </IonCol>
                  <IonCol style={{ display: "flex", alignItems: "center" }}>
                    <IonLabel>{item.operation}</IonLabel>
                  </IonCol>

                  <IonCol
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    {item.error?.length
                      ? (item.error ?? []).map((r, i) => (
                          <IonLabel
                            class={
                              i + 1 !== item.error?.length
                                ? "ion-padding-bottom"
                                : ""
                            }
                            key={r}
                            color="danger"
                          >
                            {r}
                            <br />
                          </IonLabel>
                        ))
                      : "--"}
                  </IonCol>
                  <IonCol style={{ display: "flex", alignItems: "center" }}>
                    <IonChip color={getStatusColor(item.status)}>
                      <IonLabel>{item.status}</IonLabel>
                    </IonChip>
                  </IonCol>
                  <IonCol style={{ display: "flex", alignItems: "center" }}>
                    {item.status === "failed" && item.canUpdated && (
                      <IonButton
                        onClick={() => handleUpdateData(item)}
                        fill="clear"
                        size="small"
                      >
                        <IonIcon
                          ios={pencilSharp}
                          md={pencilOutline}
                          slot="icon-only"
                        ></IonIcon>
                      </IonButton>
                    )}

                    {item.status !== "success" && (
                      <IonButton
                        onClick={() => handleDelete(item.id)}
                        color="danger"
                        fill="clear"
                        size="small"
                      >
                        <IonIcon
                          ios={trashSharp}
                          md={trashOutline}
                          slot="icon-only"
                        ></IonIcon>
                      </IonButton>
                    )}
                  </IonCol>
                </IonRow>
                {/* Divider below each row */}
                <IonRow>
                  <IonCol size="12">
                    <div
                      style={{
                        height: "1px",
                        backgroundColor: "var(--ion-color-light)",
                      }}
                    />
                  </IonCol>
                </IonRow>
              </React.Fragment>
            ))}
          </IonGrid>
        </IonContent>
      </IonPage>
      {updateTimeEntry && (
        <TimeEntry
          isOpen={!!updateTimeEntry}
          cancel={() => setUpdateTimeEntry(undefined)}
          confirm={() => {
            makeOperationCompletedById(updateTimeEntry?.id ?? ""); //complete the operation
            setUpdateTimeEntry(undefined);
          }}
          offlineTimeEntryId={updateTimeEntry?.id}
          workOrder={updateTimeEntry?.RequestObject?.WorkOrderNumber}
          finalTime={updateTimeEntry?.RequestObject?.WorkHours}
        />
      )}
    </>
  );
};

export default OfflineUploadData;
